import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminAuthContext = createContext(null);

const SUPER_ADMIN = {
  email: 'admin@tannit.uk',
  password: 'Tannit2@25',
  role: 'super_admin',
  name: 'Super Admin'
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedAdmin = localStorage.getItem('adminUser');
    const storedAdmins = localStorage.getItem('adminsList');
    
    if (storedAdmin) {
      setAdminUser(JSON.parse(storedAdmin));
    }
    
    if (storedAdmins) {
      setAdmins(JSON.parse(storedAdmins));
    }
    
    setLoading(false);
  }, []);

  const login = (email, password) => {
    if (email === SUPER_ADMIN.email && password === SUPER_ADMIN.password) {
      const user = { email: SUPER_ADMIN.email, role: SUPER_ADMIN.role, name: SUPER_ADMIN.name };
      setAdminUser(user);
      localStorage.setItem('adminUser', JSON.stringify(user));
      return { success: true, user };
    }

    const admin = admins.find(a => a.email === email && a.password === password);
    if (admin) {
      const user = { email: admin.email, role: admin.role, name: admin.name };
      setAdminUser(user);
      localStorage.setItem('adminUser', JSON.stringify(user));
      return { success: true, user };
    }

    return { success: false, error: 'Invalid email or password' };
  };

  const logout = () => {
    setAdminUser(null);
    localStorage.removeItem('adminUser');
  };

  const addAdmin = (adminData) => {
    if (adminUser?.role !== 'super_admin') {
      return { success: false, error: 'Only super admin can add admins' };
    }

    if (adminData.email === SUPER_ADMIN.email) {
      return { success: false, error: 'Cannot use super admin email' };
    }

    if (admins.find(a => a.email === adminData.email)) {
      return { success: false, error: 'Admin with this email already exists' };
    }

    const newAdmin = {
      id: Date.now(),
      ...adminData,
      role: 'admin',
      createdAt: new Date().toISOString()
    };

    const updatedAdmins = [...admins, newAdmin];
    setAdmins(updatedAdmins);
    localStorage.setItem('adminsList', JSON.stringify(updatedAdmins));
    return { success: true, admin: newAdmin };
  };

  const updateAdmin = (id, adminData) => {
    if (adminUser?.role !== 'super_admin') {
      return { success: false, error: 'Only super admin can update admins' };
    }

    const updatedAdmins = admins.map(a => 
      a.id === id ? { ...a, ...adminData, updatedAt: new Date().toISOString() } : a
    );
    setAdmins(updatedAdmins);
    localStorage.setItem('adminsList', JSON.stringify(updatedAdmins));
    return { success: true };
  };

  const deleteAdmin = (id) => {
    if (adminUser?.role !== 'super_admin') {
      return { success: false, error: 'Only super admin can delete admins' };
    }

    const updatedAdmins = admins.filter(a => a.id !== id);
    setAdmins(updatedAdmins);
    localStorage.setItem('adminsList', JSON.stringify(updatedAdmins));
    return { success: true };
  };

  const isSuperAdmin = () => adminUser?.role === 'super_admin';

  return (
    <AdminAuthContext.Provider value={{
      adminUser,
      admins,
      loading,
      login,
      logout,
      addAdmin,
      updateAdmin,
      deleteAdmin,
      isSuperAdmin,
      isAuthenticated: !!adminUser
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export default AdminAuthContext;
